import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import { selectors, types } from '@elab/core/store/store-locator';
import connect from '@elab/core/store/connect';
import { deferred } from '@elab/core/utils/webComponent';
import { link } from '@elab/core/utils/htmlTemplates';
import { actions as uiActions } from '@elab/core/store/ui';

class StoreLocatorSelectableList extends SimpleLitElement {
    static get properties() {
        return {
            stores: { type: Array, attribute: false },
            actionType: { type: String, attribute: 'action-type' },
            cssClass: { type: String, attribute: 'css-class' },
            type: { type: String, attribute: 'type' },
            isAddressSplitted: { type: Boolean, attribute: 'splitted-address' },
            isAddressVisible: { type: Boolean, attribute: 'visible-address' },
            isPhoneNumberVisible: {
                type: Boolean,
                attribute: 'visible-phone-number',
            },
            shouldScrollIntoView: {
                type: Boolean,
                attribute: 'scroll-into-view',
            },
            usePointsOfServices: {
                type: Boolean,
                attribute: 'use-points-of-services',
            },
            detailsLabel: { styles: String, attribute: 'details-label' },
            hoursTitle: { styles: String, attribute: 'hours-title' },
            pickupTitle: { styles: String, attribute: 'pickup-title' },
            showExtendedList: { type: Boolean, attribute: 'show-extended-list' },
            storeClosedLabel: { type: String, attribute: 'store-closed-label' },
            distanceLabel: { type: String, attribute: 'distance-label' },
            isPostalPickup: { type: Boolean, attribute: 'is-postal-pickup' },
            nameIconClass: { type: String, attribute: 'name-icon-class' },
            isNameIconDistance: { type: Boolean, attribute: 'name-icon-distance' },
            searchQuery: { type: String, attribute: false },
            isDistrictVisible: { type: Boolean, attribute: 'district-visible' },
            isStoreNameVisible: { type: Boolean, attribute: 'store-name-visible' },
        };
    }

    constructor() {
        super();
        this.stores = [];
        this.cssClass = '';
        this.type = 'PickupListItem';
        this.showExtendedList = false;
        this.hoursTitle = '';
        this.pickupTitle = '';
        this.storeClosedLabel = '';
        this.isAddressSplitted = false;
        this.distanceLabel = '';
        this.isPostalPickup = false;
        this.nameIconClass = '';
        this.isNameIconDistance = false;
        this.searchQuery = '';
        this.isDistrictVisible = false;
        this.isStoreNameVisible = false;
    }

    scrollMapIntoView = deferred(() => {
        this.dispatchEvent(new CustomEvent('scroll-map-into-view'));
    }, 30);

    handleClick(storeId, triggerMap = true) {
        this.dispatchEvent(
            new CustomEvent('select-store', {
                detail: { storeId, triggerMap },
            }),
        );
        this.shouldScrollIntoView && this.scrollMapIntoView();

        if (this.isPostalPickup) {
            this.dispatchEvent(
                new CustomEvent('select-post-point', {
                    detail: {
                        type: types.APPLY_DELIVERY_POST_POINT,
                    },
                }),
            );
        }
    }

    getAddressLineTemplate(address) {
        return html`
            <span class="store-locator__address-line">${address.line1} ${address.line2}</span>
        `;
    }

    getPickupListItemTemplate = ({
        name,
        displayName,
        address = {},
        openingHours = {},
        distanceKm = '',
    }) => {
        const addressDelimeter = !this.isAddressSplitted ? ',' : '';
        return this.showExtendedList
            ? this.getExtendedListTemplate(name, address, openingHours, distanceKm, displayName)
            : html`
                  <div
                      class="store-locator ${this.cssClass}"
                      @click="${() => this.handleClick(name)}"
                  >
                      <div class="store-locator__primary-address">
                          ${address.postalCode || ''} ${address.town || ''}${addressDelimeter}
                          ${!this.isAddressSplitted ? this.getAddressLineTemplate(address) : ''}
                      </div>

                      ${this.isAddressSplitted
                          ? html`
                                <div class="store-locator__additional-address">
                                    ${this.getAddressLineTemplate(address)}
                                </div>
                            `
                          : ''}
                  </div>
              `;
    };

    getExtendedListTemplate = (name, address, openingHours, distanceKm, displayName) => {
        const addressDelimeter = !this.isAddressSplitted ? ',' : '';
        return html`
            <div class="store-locator ${this.cssClass}">
                <p class="store-locator__name">${displayName}</p>
                <div class="store-locator__primary-address">
                    ${address.postalCode || ''} ${address.town || ''}${addressDelimeter}
                    ${!this.isAddressSplitted ? this.getAddressLineTemplate(address) : ''}
                </div>

                ${this.isAddressSplitted
                    ? html`
                          <div class="store-locator__additional-address">
                              ${this.getAddressLineTemplate(address)}
                          </div>
                      `
                    : ''}
                ${distanceKm
                    ? html`
                          <div class="store-locator__distance">
                              ${distanceKm} ${this.distanceLabel}
                          </div>
                      `
                    : ''}

                <div class="store-locator__hours">
                    <div class="store-locator__title">${this.hoursTitle}</div>
                    <e2-hours-table
                        .openHours="${openingHours.weekDayOpeningList}"
                        css-class="table--near-store"
                        closed-label="${this.storeClosedLabel}"
                    ></e2-hours-table>
                </div>
                <div
                    class="button store-locator__pickup-button button--full-width"
                    @click="${() => this.handleClick(name, false)}"
                >
                    ${this.pickupTitle}
                </div>
            </div>
        `;
    };

    getClickableStoreCardTemplate = ({ name, address = {} }) => {
        return html`
            <div class="store-locator ${this.cssClass}" @click="${() => this.handleClick(name)}">
                <div class="address-card">
                    <div class="address-card__address">
                        <span class="address-card__address--town">
                            ${address.town}
                        </span>

                        ${this.isDistrictVisible && address.district
                            ? html`
                                  <span class="address-card__address--district">
                                      ${address.district}
                                  </span>
                              `
                            : null}
                        ${this.isStoreNameVisible && name
                            ? html`
                                  <span class="address-card__address--store-name">${name}</span>
                              `
                            : null}
                        ${this.isAddressVisible
                            ? html`
                                  <span class="address-card__address--line1">${address.line1}</span>
                              `
                            : null}
                    </div>
                </div>
            </div>
        `;
    };

    getStoreCardTemplate = ({ name, url, address = {}, distanceKm = '' }) => {
        return html`
            <div class="store-locator ${this.cssClass}">
                <h4 class="store-locator__store-name" @click="${() => this.handleClick(name)}">
                    ${this.nameIconClass
                        ? html`
                              <i class="${this.nameIconClass}"></i>
                          `
                        : html``}
                    ${this.isNameIconDistance && this.searchQuery && distanceKm
                        ? html`
                              <span class="store-locator__store-distance">
                                  <span class="store-locator__store-distance--value">
                                      ${distanceKm.toString().replace('.', ',')}
                                  </span>
                                  <span class="store-locator__store-distance--measure">
                                      km
                                  </span>
                              </span>
                          `
                        : html``}
                    ${name}
                </h4>

                <div class="address-card">
                    <div class="address-card__address">
                        ${this.isAddressVisible
                            ? html`
                                  <span class="address-card__address--line1">${address.line1}</span>
                                  <span class="address-card__address--postalCode">
                                      ${address.postalCode}
                                  </span>
                              `
                            : null}
                        <span class="address-card__address--town">
                            ${address.town}
                        </span>
                    </div>
                </div>
                ${this.isPhoneNumberVisible
                    ? html`
                          <div class="phone-card">
                              <div class="phone-card__number">
                                  ${address.phone}
                              </div>
                          </div>
                      `
                    : null}

                <div class="store-locator__footer">
                    ${link({ className: 'store-locator__link', href: url }, this.detailsLabel)}
                </div>
            </div>
        `;
    };

    render() {
        return html`
            ${this.stores.map(this[`get${this.type}Template`])}
        `;
    }
}

const mapStateToProps = state => ({
    stores: selectors.getPaginatedStores(state),
    searchQuery: selectors.getQueryApiParameter(state),
});

const mapDispatchToEvents = (dispatch, ctx) => ({
    'select-store': ({ detail }) =>
        dispatch({
            type: ctx.actionType,
            payload: detail,
        }),
    'scroll-map-into-view': () =>
        dispatch(
            uiActions.scrollToTop({
                target: '.store-page__map',
            }),
        ),
    'select-post-point': ({ detail }) => {
        dispatch({ type: detail.type, payload: detail });
    },
});

export default () =>
    customElements.define(
        'e2-store-locator-selectable-list',
        connect(mapStateToProps, mapDispatchToEvents)(StoreLocatorSelectableList),
    );
